import {
  ActionIcon,
  Badge,
  Button,
  FileButton,
  Group,
  Menu,
  Modal,
  PasswordInput,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconCheck,
  IconDots,
  IconEye,
  IconEyeOff,
  IconLink,
  IconPencil,
  IconPlus,
  IconRobot,
  IconRobotOff,
  IconStar,
  IconStarFilled,
  IconTrash,
} from "@tabler/icons-react";
import { useSpaceQuery } from "@/features/space/queries/space-query.ts";
import {
  getSpaceById,
  updateSpace,
} from "@/features/space/services/space-service.ts";
import { getSidebarPages } from "@/features/page/services/page-service.ts";
import { queryClient } from "@/main.tsx";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { notifications } from "@mantine/notifications";
import classes from "./tabs-strip.module.css";
import {
  useCreateTabMutation,
  useDeleteTabMutation,
  useTabsQuery,
  useUpdateTabMutation,
} from "../queries/tab-query.ts";
import { activeTabBySpaceAtom } from "../atoms/active-tab-atom.ts";
import type { ITab, IParsedSpec, TabKind } from "../types/tab.types.ts";
import { fetchTabSpecFromUrl, parseTabSpec } from "../services/tab-service.ts";
import { listOperationsFromSpec } from "../utils/openapi-ops.ts";
import { OpenApiTabModal } from "./openapi-tab-modal.tsx";

// Reuses the design-system api-reference-v3 palette so admin badges
// match the public sidebar method labels.
function methodColor(method: string): string {
  switch (method) {
    case "get":
      return "teal";
    case "post":
      return "blue";
    case "put":
      return "orange";
    case "patch":
      return "violet";
    case "delete":
      return "red";
    default:
      return "gray";
  }
}

type Props = {
  spaceId: string;
  canManage: boolean;
};

/**
 * Sidebar tabs strip — mirrors the public Layout Tabs UX inside the admin.
 *
 * Selecting a tab scopes the page tree below to that tab's content. The
 * "loose" pill is rendered only when there are pages that aren't yet
 * inside any tab (so editors can find and reassign them).
 */
export function TabsStrip({ spaceId, canManage }: Props) {
  const { t } = useTranslation();
  const { data: tabs = [], isLoading } = useTabsQuery(spaceId);
  const [activeBySpace, setActiveBySpace] = useAtom(activeTabBySpaceAtom);
  const active = activeBySpace[spaceId] ?? null;
  const [createOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);

  // When the tab list resolves, pick a sensible initial active tab.
  useEffect(() => {
    if (isLoading) return;
    if (active && active !== "loose") {
      // Validate it still exists
      if (!tabs.some((t) => t.id === active)) {
        const fallback = tabs.find((t) => t.isDefault) ?? tabs[0];
        setActiveBySpace((prev) => ({
          ...prev,
          [spaceId]: fallback?.id ?? null,
        }));
      }
      return;
    }
    if (active == null && tabs.length > 0) {
      const initial = tabs.find((t) => t.isDefault) ?? tabs[0];
      setActiveBySpace((prev) => ({ ...prev, [spaceId]: initial.id }));
    }
  }, [tabs, isLoading, active, spaceId, setActiveBySpace]);

  const setActive = (next: string | "loose" | null) => {
    setActiveBySpace((prev) => ({ ...prev, [spaceId]: next }));
  };

  if (!isLoading && tabs.length === 0 && !canManage) return null;

  return (
    <div className={classes.strip}>
      <ScrollArea
        type="never"
        offsetScrollbars={false}
        style={{ flex: 1, minWidth: 0 }}
      >
        <Group gap={4} wrap="nowrap" className={classes.tabsRow}>
          {tabs.map((tab) => (
            <TabPill
              key={tab.id}
              tab={tab}
              active={active === tab.id}
              canManage={canManage}
              spaceId={spaceId}
              visibleCount={tabs.filter((t) => !t.isHidden).length}
              totalCount={tabs.length}
              onSelect={() => setActive(tab.id)}
            />
          ))}
        </Group>
      </ScrollArea>

      {canManage && (
        <Tooltip label={t("New tab")} withArrow position="top">
          <ActionIcon
            variant="default"
            size={22}
            onClick={openCreate}
            aria-label={t("New tab")}
          >
            <IconPlus size={14} />
          </ActionIcon>
        </Tooltip>
      )}

      <CreateTabModal
        opened={createOpened}
        onClose={closeCreate}
        spaceId={spaceId}
        firstTab={tabs.length === 0}
        onCreated={(tab) => {
          setActive(tab.id);
        }}
      />
    </div>
  );
}

function TabPill({
  tab,
  active,
  canManage,
  spaceId,
  visibleCount,
  totalCount,
  onSelect,
}: {
  tab: ITab;
  active: boolean;
  canManage: boolean;
  spaceId: string;
  // Number of non-hidden tabs in the same space. Drives the
  // "Hide from public" disabled state — can't hide the only visible tab.
  visibleCount: number;
  // Total active tabs (incl. hidden). Drives "Delete tab" disabled state —
  // backend enforces last-tab guard, mirror it in UI.
  totalCount: number;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const [renameOpened, { open: openRename, close: closeRename }] =
    useDisclosure(false);
  const updateTab = useUpdateTabMutation(spaceId);
  const deleteTab = useDeleteTabMutation(spaceId);
  const { data: space } = useSpaceQuery(spaceId);
  const [previewBusy, setPreviewBusy] = useState(false);

  // One-click manager link that deep-links into THIS tab on the live
  // public site without publishing it. Lazily provisions the space-wide
  // preview token if missing, then:
  //   - openapi tab → /openapi/<tabId>?preview=<token>
  //   - regular tab → /<firstPageSlug>?preview=<token>  (first page in tab)
  //   - empty tab   → /?preview=<token> + informational toast
  const copyPreviewLink = async () => {
    setPreviewBusy(true);
    try {
      let currentSpace = space;
      let token = currentSpace?.settings?.previewToken ?? null;
      if (!token) {
        await updateSpace({
          spaceId,
          // @ts-ignore — backend accepts this on UpdateSpaceDto
          regeneratePreviewToken: true,
        });
        const fresh = await getSpaceById(spaceId);
        await queryClient.invalidateQueries({ queryKey: ["space", spaceId] });
        currentSpace = fresh ?? currentSpace;
        token = fresh?.settings?.previewToken ?? null;
      }
      if (!token) {
        notifications.show({
          message: t("Failed to create preview link"),
          color: "red",
        });
        return;
      }
      const host =
        currentSpace?.customDomain || `${currentSpace?.slug}.commitdoc.com`;

      // Per-tab deep link
      let path = "/";
      let emptyTab = false;
      if (tab.kind === "openapi") {
        path = `/openapi/${tab.id}`;
      } else {
        try {
          const page = await getSidebarPages({
            spaceId,
            tabId: tab.id,
            limit: 1,
          });
          const first = page?.items?.[0] as any;
          const segment = first?.publicSlug || first?.slugId;
          if (segment) {
            path = `/${segment}`;
          } else {
            emptyTab = true;
          }
        } catch {
          // fall back to space root — at least the token will still work
        }
      }
      const link = `https://${host}${path}?preview=${token}`;

      if (!navigator.clipboard) {
        notifications.show({
          message: t("Clipboard unavailable in this browser context"),
          color: "red",
        });
        return;
      }
      await navigator.clipboard.writeText(link);
      notifications.show({
        message: emptyTab
          ? t(
              "Preview link copied — tab has no pages yet, link opens the space root",
            )
          : t(
              "Preview link copied — reveals this tab without publishing it",
            ),
        color: emptyTab ? "yellow" : "green",
      });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.message ?? t("Failed to create preview link"),
        color: "red",
      });
    } finally {
      setPreviewBusy(false);
    }
  };

  const setDefault = () => {
    updateTab.mutate(
      { tabId: tab.id, isDefault: true },
      {
        onSuccess: () =>
          notifications.show({ message: t("Default tab updated") }),
      },
    );
  };

  const toggleHidden = () => {
    updateTab.mutate(
      { tabId: tab.id, isHidden: !tab.isHidden },
      {
        onSuccess: () =>
          notifications.show({
            message: tab.isHidden ? t("Tab visible") : t("Tab hidden"),
          }),
      },
    );
  };

  const toggleAskAiExcluded = () => {
    const next = !(tab as any).askAiExcluded;
    updateTab.mutate(
      // Field is camelCase end-to-end; the backend DTO accepts it as
      // `askAiExcluded` and persists to `tabs.ask_ai_excluded`.
      { tabId: tab.id, askAiExcluded: next } as any,
      {
        onSuccess: () =>
          notifications.show({
            message: next
              ? t("Tab excluded from Ask AI — reindex queued")
              : t("Tab included in Ask AI — reindex queued"),
          }),
      },
    );
  };

  const remove = () => {
    modals.openConfirmModal({
      title: t("Delete tab"),
      centered: true,
      children: (
        <Text size="sm">
          {t(
            'Delete tab "{{name}}"? Pages inside it will move into the default tab; if this is the last tab they become unassigned.',
            { name: tab.name },
          )}
        </Text>
      ),
      labels: { confirm: t("Delete tab"), cancel: t("Cancel") },
      confirmProps: { color: "red" },
      onConfirm: () =>
        deleteTab.mutate(tab.id, {
          onSuccess: () => notifications.show({ message: t("Tab deleted") }),
          onError: (err: any) =>
            notifications.show({
              message:
                err?.response?.data?.message ?? t("Failed to delete tab"),
              color: "red",
            }),
        }),
    });
  };

  return (
    <>
      <UnstyledButton
        className={`${classes.pill} ${active ? classes.pillActive : ""} ${tab.isHidden ? classes.pillHidden : ""}`}
        onClick={onSelect}
      >
        <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} truncate>
            {tab.name}
          </Text>
          {tab.kind === "openapi" ? (
            <Badge size="xs" variant="light" color="teal" radius="sm">
              {t("API")}
            </Badge>
          ) : null}
          {tab.kind === "openapi" &&
          // Pulled-after-published timestamp comparison is a cheap proxy
          // for "draft has changes vs published" — works for both the
          // cron pull and any manual paste/upload (which also bumps the
          // updatedAt). False positives (pull yields identical bytes)
          // are harmless; the diff dialog will show "nothing to publish"
          // and the editor closes the modal.
          tab.specSourcePulledAt &&
          tab.openapiPublishedAt &&
          new Date(tab.specSourcePulledAt) >
            new Date(tab.openapiPublishedAt) ? (
            <Tooltip
              label={t(
                "Draft has updates not yet published. Open Edit → Publish to review.",
              )}
              withArrow
              openDelay={250}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: "var(--mantine-color-orange-6)",
                  display: "inline-block",
                  flexShrink: 0,
                }}
                aria-label={t("Draft updates available")}
              />
            </Tooltip>
          ) : null}
          {tab.isDefault ? (
            <IconStarFilled
              size={11}
              color="var(--mantine-color-yellow-filled)"
            />
          ) : null}
          {tab.isHidden ? (
            <IconEyeOff size={11} opacity={0.7} />
          ) : null}
          {canManage && (
            <Menu
              shadow="md"
              width={180}
              position="bottom-end"
              withinPortal
              onOpen={(e?: any) => e?.stopPropagation?.()}
            >
              <Menu.Target>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t("Tab menu")}
                >
                  <IconDots size={12} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openRename();
                  }}
                >
                  {t("Rename / edit description")}
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    tab.isDefault ? (
                      <IconStarFilled size={14} />
                    ) : (
                      <IconStar size={14} />
                    )
                  }
                  disabled={tab.isDefault}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDefault();
                  }}
                >
                  {tab.isDefault ? t("Default tab") : t("Set as default")}
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    tab.isHidden ? (
                      <IconEye size={14} />
                    ) : (
                      <IconEyeOff size={14} />
                    )
                  }
                  // Hiding the only visible tab leaves the public site
                  // with nothing to render — backend rejects, mirror in UI.
                  disabled={!tab.isHidden && visibleCount <= 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHidden();
                  }}
                >
                  {tab.isHidden ? t("Show in public") : t("Hide from public")}
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    (tab as any).askAiExcluded ? (
                      <IconRobot size={14} />
                    ) : (
                      <IconRobotOff size={14} />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAskAiExcluded();
                  }}
                >
                  {(tab as any).askAiExcluded
                    ? t("Include in Ask AI")
                    : t("Exclude from Ask AI")}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconLink size={14} />}
                  disabled={previewBusy}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyPreviewLink();
                  }}
                >
                  {t("Copy preview link")}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  disabled={totalCount <= 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    remove();
                  }}
                >
                  {t("Delete tab")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </UnstyledButton>

      {tab.kind === "openapi" ? (
        <OpenApiTabModal
          opened={renameOpened}
          onClose={closeRename}
          tab={tab}
          spaceId={spaceId}
        />
      ) : (
        <RenameTabModal
          opened={renameOpened}
          onClose={closeRename}
          tab={tab}
          spaceId={spaceId}
        />
      )}
    </>
  );
}

function CreateTabModal({
  opened,
  onClose,
  spaceId,
  firstTab,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  spaceId: string;
  firstTab: boolean;
  onCreated: (tab: ITab) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(firstTab);
  const [kind, setKind] = useState<TabKind>("content");
  const [specText, setSpecText] = useState("");
  const [parsedSpec, setParsedSpec] = useState<IParsedSpec | null>(null);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  // Source URL inputs — only meaningful when kind === 'openapi'. The URL
  // is persisted on the tab when create succeeds so subsequent Pulls
  // (manual or cron) work without an extra Edit round-trip.
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceAuth, setSourceAuth] = useState("");
  const [fetching, setFetching] = useState(false);
  const create = useCreateTabMutation();

  useEffect(() => {
    if (opened) {
      setName("");
      setDescription("");
      setIsDefault(firstTab);
      setKind("content");
      setSpecText("");
      setParsedSpec(null);
      setParseErr(null);
      setSourceUrl("");
      setSourceAuth("");
    }
  }, [opened, firstTab]);

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    const txt = await file.text();
    setSpecText(txt);
    setParsedSpec(null);
    setParseErr(null);
  };

  const validateSpec = async () => {
    setValidating(true);
    setParseErr(null);
    try {
      const r = await parseTabSpec(specText);
      setParsedSpec(r);
    } catch (err: any) {
      setParseErr(err?.response?.data?.message ?? String(err));
      setParsedSpec(null);
    } finally {
      setValidating(false);
    }
  };

  const fetchFromUrl = async () => {
    setFetching(true);
    setParseErr(null);
    try {
      const r = await fetchTabSpecFromUrl(sourceUrl.trim(), sourceAuth.trim());
      setParsedSpec(r);
    } catch (err: any) {
      setParseErr(err?.response?.data?.message ?? String(err));
      setParsedSpec(null);
    } finally {
      setFetching(false);
    }
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (kind === "openapi" && !parsedSpec) {
      setParseErr(t("Validate the spec before saving"));
      return;
    }
    create.mutate(
      {
        spaceId,
        name: trimmed,
        description: description.trim() || undefined,
        isDefault,
        kind,
        openapiSpec: kind === "openapi" ? parsedSpec?.spec : null,
        // Persist source URL on the new tab so Pull-now works on day one
        // without a follow-up Edit save. Only stored for openapi tabs.
        specSourceUrl:
          kind === "openapi" ? (sourceUrl.trim() || null) : null,
        specSourceAuth:
          kind === "openapi" ? (sourceAuth.trim() || null) : null,
      },
      {
        onSuccess: (tab) => {
          notifications.show({ message: t("Tab created") });
          onCreated(tab);
          onClose();
        },
        onError: (err: any) => {
          notifications.show({
            message:
              err?.response?.data?.message ?? t("Failed to create tab"),
            color: "red",
          });
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("New tab")}
      centered
      size="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t(
            'A "Content" tab owns a tree of pages. An "OpenAPI" tab renders a 3‑column API reference from a spec — no pages required.',
          )}
        </Text>
        <Select
          label={t("Type")}
          value={kind}
          onChange={(v) => v && setKind(v as TabKind)}
          data={[
            { value: "content", label: t("Content (page tree)") },
            { value: "openapi", label: t("OpenAPI (3‑column reference)") },
          ]}
          allowDeselect={false}
        />
        <TextInput
          label={t("Name")}
          placeholder={
            kind === "openapi"
              ? t("e.g. API, REST API, Live API")
              : t("e.g. v2, v1, Public API")
          }
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          data-autofocus
          required
        />
        <TextInput
          label={t("Description")}
          placeholder={t("Shown under the tab title in the dropdown")}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Switch
          label={t("Default tab (root URL lands here)")}
          checked={isDefault}
          onChange={(e) => setIsDefault(e.currentTarget.checked)}
        />

        {kind === "openapi" && (
          <Stack gap={6}>
            <Text size="sm" fw={500}>
              {t("OpenAPI spec")}
            </Text>
            <Text size="xs" c="dimmed">
              {t(
                "Either fetch from a URL (recommended — saves URL on the tab so subsequent Pulls work) or upload / paste once.",
              )}
            </Text>
            <TextInput
              size="xs"
              label={t("Spec URL (optional)")}
              placeholder="https://raw.githubusercontent.com/<org>/<repo>/main/openapi.yaml"
              value={sourceUrl}
              onChange={(e) => {
                setSourceUrl(e.currentTarget.value);
                setParsedSpec(null);
                setParseErr(null);
              }}
            />
            <PasswordInput
              size="xs"
              label={t("Bearer token (optional)")}
              placeholder={t("For private repos / gated URLs")}
              value={sourceAuth}
              onChange={(e) => setSourceAuth(e.currentTarget.value)}
              autoComplete="off"
            />
            <Group gap="xs">
              <Button
                size="xs"
                variant="filled"
                onClick={fetchFromUrl}
                loading={fetching}
                disabled={!sourceUrl.trim()}
              >
                {t("Fetch & validate")}
              </Button>
              <Text size="xs" c="dimmed">
                {t("…or upload / paste manually below.")}
              </Text>
            </Group>
            <Group gap="xs">
              <FileButton
                accept=".json,.yaml,.yml,application/json,application/x-yaml,text/yaml"
                onChange={onPickFile}
              >
                {(props) => (
                  <Button {...props} variant="default" size="xs">
                    {t("Upload file")}
                  </Button>
                )}
              </FileButton>
              <Text size="xs" c="dimmed">
                {t("…or paste below (JSON or YAML).")}
              </Text>
            </Group>
            <Textarea
              minRows={6}
              maxRows={12}
              autosize
              placeholder={"openapi: 3.0.0\ninfo: { ... }\npaths: { ... }"}
              value={specText}
              onChange={(e) => {
                setSpecText(e.currentTarget.value);
                setParsedSpec(null);
                setParseErr(null);
              }}
              styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
            />
            <Group gap="xs">
              <Button
                variant="light"
                size="xs"
                onClick={validateSpec}
                loading={validating}
                disabled={!specText.trim()}
              >
                {t("Validate")}
              </Button>
              {parsedSpec && (
                <Text size="xs" c="green">
                  ✓ {parsedSpec.info.title ?? t("OpenAPI")} v
                  {parsedSpec.info.version ?? "?"} — {parsedSpec.paths}{" "}
                  {t("operations")}
                </Text>
              )}
              {parseErr && (
                <Text size="xs" c="red" style={{ flex: 1, minWidth: 0 }}>
                  {parseErr}
                </Text>
              )}
            </Group>
          </Stack>
        )}

        <Group justify="flex-end" gap="xs">
          <Button
            variant="default"
            onClick={onClose}
            disabled={create.isPending}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={create.isPending}
            disabled={
              !name.trim() ||
              (kind === "openapi" && !parsedSpec)
            }
          >
            {t("Create")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RenameTabModal({
  opened,
  onClose,
  tab,
  spaceId,
}: {
  opened: boolean;
  onClose: () => void;
  tab: ITab;
  spaceId: string;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(tab.name);
  const [description, setDescription] = useState(tab.description ?? "");
  // OpenAPI-only spec replacement state. Empty `specText` means "leave the
  // current spec untouched" — that's how editors do small renames without
  // re-uploading the spec every time.
  const [specText, setSpecText] = useState("");
  const [parsedSpec, setParsedSpec] = useState<IParsedSpec | null>(null);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  // Per-operation visibility toggles. Mirrors `tabs.openapi_hidden_ops`
  // — array of slugs that the editor wants hidden from the public site.
  // We keep this as a Set client-side to make toggle/clear cheap, then
  // serialize back to an array on submit.
  const [hiddenOps, setHiddenOps] = useState<Set<string>>(
    new Set(tab.openapiHiddenOps ?? []),
  );
  const [opsFilter, setOpsFilter] = useState("");
  const update = useUpdateTabMutation(spaceId);
  const isOpenApi = tab.kind === "openapi";
  const currentSpecInfo = useMemo(() => {
    if (!isOpenApi || !tab.openapiSpec) return null;
    const info = (tab.openapiSpec as any).info ?? {};
    const paths = Object.keys((tab.openapiSpec as any).paths ?? {}).length;
    return {
      title: typeof info.title === "string" ? info.title : null,
      version: typeof info.version === "string" ? info.version : null,
      paths,
    };
  }, [isOpenApi, tab.openapiSpec]);

  useEffect(() => {
    if (opened) {
      setName(tab.name);
      setDescription(tab.description ?? "");
      setSpecText("");
      setParsedSpec(null);
      setParseErr(null);
      setHiddenOps(new Set(tab.openapiHiddenOps ?? []));
      setOpsFilter("");
    }
  }, [opened, tab.id, tab.name, tab.description, tab.openapiHiddenOps]);

  // List of all operations from the *currently saved* spec, plus any
  // operations from a freshly validated paste (so editors can adjust
  // visibility before saving the new spec). Grouped by tag.
  const operationsList = useMemo(() => {
    if (!isOpenApi) return [];
    const spec = parsedSpec?.spec ?? tab.openapiSpec;
    return listOperationsFromSpec(spec ?? null);
  }, [isOpenApi, parsedSpec, tab.openapiSpec]);

  const filteredOps = useMemo(() => {
    const q = opsFilter.trim().toLowerCase();
    if (!q) return operationsList;
    return operationsList.filter(
      (op) =>
        op.title.toLowerCase().includes(q) ||
        op.path.toLowerCase().includes(q) ||
        op.method.toLowerCase().includes(q) ||
        (op.tag ?? "").toLowerCase().includes(q),
    );
  }, [operationsList, opsFilter]);

  const operationsByGroup = useMemo(() => {
    const groups = new Map<
      string,
      ReturnType<typeof listOperationsFromSpec>
    >();
    for (const op of filteredOps) {
      const k = op.tag ?? "—";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(op);
    }
    return Array.from(groups.entries());
  }, [filteredOps]);

  const toggleOp = (slug: string) => {
    setHiddenOps((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };
  const setAllHidden = (hidden: boolean) => {
    setHiddenOps((prev) => {
      const next = new Set(prev);
      for (const op of filteredOps) {
        if (hidden) next.add(op.slug);
        else next.delete(op.slug);
      }
      return next;
    });
  };

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    const txt = await file.text();
    setSpecText(txt);
    setParsedSpec(null);
    setParseErr(null);
  };

  const validateSpec = async () => {
    setValidating(true);
    setParseErr(null);
    try {
      const r = await parseTabSpec(specText);
      setParsedSpec(r);
    } catch (err: any) {
      setParseErr(err?.response?.data?.message ?? String(err));
      setParsedSpec(null);
    } finally {
      setValidating(false);
    }
  };

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const desc = description.trim();
    // If editor pasted/uploaded a new spec, require successful validation
    // before save — same gate as Create modal so we never persist garbage.
    if (isOpenApi && specText.trim() && !parsedSpec) {
      setParseErr(t("Validate the spec before saving"));
      return;
    }
    update.mutate(
      {
        tabId: tab.id,
        name: trimmed,
        description: desc.length ? desc : null,
        ...(isOpenApi && parsedSpec
          ? { openapiSpec: parsedSpec.spec }
          : {}),
        ...(isOpenApi
          ? { openapiHiddenOps: Array.from(hiddenOps) }
          : {}),
      },
      {
        onSuccess: () => {
          notifications.show({ message: t("Tab updated") });
          onClose();
        },
        onError: () =>
          notifications.show({
            message: t("Failed to update tab"),
            color: "red",
          }),
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("Edit tab")}
      centered
      size={isOpenApi ? "lg" : "md"}
    >
      <Stack gap="sm">
        <TextInput
          label={t("Name")}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
        />
        <TextInput
          label={t("Description")}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />

        {isOpenApi && (
          <Stack gap={6}>
            <Group gap={6} align="baseline">
              <Text size="sm" fw={500}>
                {t("OpenAPI spec")}
              </Text>
              {currentSpecInfo && (
                <Text size="xs" c="dimmed">
                  {t("Current")}:{" "}
                  {currentSpecInfo.title ?? t("OpenAPI")}{" "}
                  {currentSpecInfo.version
                    ? `v${currentSpecInfo.version}`
                    : ""}{" "}
                  — {currentSpecInfo.paths} {t("paths")}
                </Text>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {t(
                "Paste a new spec or upload a file to replace it. Leave empty to keep the current one.",
              )}
            </Text>
            <Group gap="xs">
              <FileButton
                accept=".json,.yaml,.yml,application/json,application/x-yaml,text/yaml"
                onChange={onPickFile}
              >
                {(props) => (
                  <Button {...props} variant="default" size="xs">
                    {t("Upload file")}
                  </Button>
                )}
              </FileButton>
              <Text size="xs" c="dimmed">
                {t("…or paste below (JSON or YAML).")}
              </Text>
            </Group>
            <Textarea
              minRows={6}
              maxRows={12}
              autosize
              placeholder={"openapi: 3.0.0\ninfo: { ... }\npaths: { ... }"}
              value={specText}
              onChange={(e) => {
                setSpecText(e.currentTarget.value);
                setParsedSpec(null);
                setParseErr(null);
              }}
              styles={{ input: { fontFamily: "monospace", fontSize: 12 } }}
            />
            <Group gap="xs">
              <Button
                variant="light"
                size="xs"
                onClick={validateSpec}
                loading={validating}
                disabled={!specText.trim()}
              >
                {t("Validate")}
              </Button>
              {parsedSpec && (
                <Text size="xs" c="green">
                  ✓ {parsedSpec.info.title ?? t("OpenAPI")} v
                  {parsedSpec.info.version ?? "?"} — {parsedSpec.paths}{" "}
                  {t("operations")}
                </Text>
              )}
              {parseErr && (
                <Text size="xs" c="red" style={{ flex: 1, minWidth: 0 }}>
                  {parseErr}
                </Text>
              )}
            </Group>
          </Stack>
        )}

        {isOpenApi && operationsList.length > 0 && (
          <Stack gap={6}>
            <Group gap={6} align="baseline" justify="space-between">
              <Text size="sm" fw={500}>
                {t("Visible operations")}
              </Text>
              <Text size="xs" c="dimmed">
                {operationsList.length - hiddenOps.size}/
                {operationsList.length} {t("shown")}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {t(
                "Toggle off to hide an operation from the public sidebar and direct links.",
              )}
            </Text>
            <Group gap="xs">
              <TextInput
                placeholder={t("Filter by name, path, method or tag")}
                size="xs"
                value={opsFilter}
                onChange={(e) => setOpsFilter(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                variant="default"
                size="xs"
                onClick={() => setAllHidden(false)}
                disabled={filteredOps.every((o) => !hiddenOps.has(o.slug))}
              >
                {t("Show all")}
              </Button>
              <Button
                variant="default"
                size="xs"
                onClick={() => setAllHidden(true)}
                disabled={filteredOps.every((o) => hiddenOps.has(o.slug))}
              >
                {t("Hide all")}
              </Button>
            </Group>
            <ScrollArea h={260} type="auto" offsetScrollbars>
              <Stack gap={4}>
                {operationsByGroup.map(([groupName, ops]) => (
                  <Stack gap={2} key={groupName}>
                    <Text size="xs" c="dimmed" fw={600} mt={6}>
                      {groupName}
                    </Text>
                    {ops.map((op) => {
                      const isHidden = hiddenOps.has(op.slug);
                      return (
                        <Group
                          key={op.slug}
                          gap="xs"
                          justify="space-between"
                          wrap="nowrap"
                          style={{
                            opacity: isHidden ? 0.55 : 1,
                            padding: "4px 6px",
                            borderRadius: 6,
                          }}
                        >
                          <Group
                            gap={8}
                            wrap="nowrap"
                            style={{ minWidth: 0, flex: 1 }}
                          >
                            <Badge
                              size="xs"
                              variant="light"
                              radius="sm"
                              color={methodColor(op.method)}
                              style={{
                                fontFamily: "monospace",
                                minWidth: 52,
                                textAlign: "center",
                              }}
                            >
                              {op.method.toUpperCase()}
                            </Badge>
                            <Tooltip label={op.path} openDelay={400}>
                              <Text
                                size="sm"
                                truncate
                                style={{ minWidth: 0 }}
                              >
                                {op.title}
                              </Text>
                            </Tooltip>
                          </Group>
                          <Switch
                            checked={!isHidden}
                            onChange={() => toggleOp(op.slug)}
                            size="xs"
                          />
                        </Group>
                      );
                    })}
                  </Stack>
                ))}
                {operationsByGroup.length === 0 && (
                  <Text size="xs" c="dimmed">
                    {t("No operations match the filter.")}
                  </Text>
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose} disabled={update.isPending}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={update.isPending}
            disabled={
              !name.trim() ||
              (isOpenApi && specText.trim().length > 0 && !parsedSpec)
            }
            leftSection={<IconCheck size={16} />}
          >
            {t("Save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
